import type { ViewEndpoint } from "../protocol/events";
import { subscribeSession } from "../shared/scan-session";
import { element } from "./dom";
import type { FindSummary } from "./find-log";
import { formatFindRange } from "./find-prices";

/** Session values are independent of persistence limits and saved-history filters. */
export function mountSessionSummary(summary: () => FindSummary): void {
  const total = element("sessionTotal");
  const badge = element("scannedBadge");
  const scans = element("scansBtn");
  let previousCount: number | undefined;
  let previousValue: string | undefined;
  badge.setAttribute("aria-hidden", "true");
  const update = (): void => {
    const current = summary();
    const values = current.values.map(formatFindRange);
    let value = values.length ? values.join(" · ") : "—";
    if (current.unpriced && values.length) value += ` · ${current.unpriced} unpriced`;
    if (value !== previousValue) {
      total.textContent = value;
      total.setAttribute("aria-label", `Session potential value: ${value}`);
      previousValue = value;
    }
    if (current.count !== previousCount) {
      badge.textContent = String(current.count);
      badge.hidden = current.count === 0;
      scans.hidden = current.count === 0;
      scans.setAttribute(
        "aria-label",
        current.count
          ? `Scans, ${current.count} ${current.count === 1 ? "card" : "cards"} scanned this session`
          : "Scans",
      );
      previousCount = current.count;
    }
  };
  // Finds and later review changes both arrive through the session store. Listening to
  // `found` as well would render the same arrival twice.
  subscribeSession(update);
  update();
}

export function mountStatus(
  endpoint: ViewEndpoint,
  root: HTMLElement,
  text: HTMLElement,
  reload: HTMLButtonElement,
): void {
  endpoint.on("status", (status) => {
    if (root.dataset.kind !== status.kind) root.dataset.kind = status.kind;
    // Find arrivals carry normal progress. Interrupt only when the person needs to
    // move the card, recover from an error, or wait for the device to hand the camera back.
    const hidden = !["closer", "exhausted", "error", "crashed", "paused"].includes(status.kind);
    if (root.hidden !== hidden) root.hidden = hidden;
    if (!hidden && text.textContent !== status.text) text.textContent = status.text;
    const hideReload = status.kind !== "crashed";
    if (reload.hidden !== hideReload) reload.hidden = hideReload;
  });
  const hide = (): void => {
    if (!root.hidden) root.hidden = true;
  };
  endpoint.on("found", hide);
  endpoint.on("session:started", hide);
  endpoint.on("session:stopped", hide);
  reload.onclick = () => location.reload();
}
